import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { X, Cookie, Settings } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  hasConsent,
  acceptAll,
  rejectAll,
  saveConsent,
  getConsent,
} from '@/lib/analytics/cookieConsent';

/**
 * Bannière de consentement cookies RGPD
 *
 * Affiche un bandeau en bas de page si l'utilisateur n'a pas encore
 * donné son consentement. Conforme au RGPD.
 */
export function CookieBanner() {
  const { t } = useTranslation('cookies');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || 'fr';

  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    // Afficher le banner uniquement si pas de consentement
    const consent = getConsent();
    if (!hasConsent()) {
      setIsVisible(true);
    }

    // Initialiser les toggles avec les préférences actuelles
    if (consent) {
      setAnalyticsEnabled(consent.analytics);
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent({
      analytics: analyticsEnabled,
      marketing: false,
    });
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 animate-slide-up">
      <Card className="max-w-2xl mx-auto bg-white shadow-xl border border-aspexit-green/20">
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <Cookie className="w-4 h-4 text-aspexit-green flex-shrink-0" />
              <h2 className="text-sm font-heading font-bold text-aspexit-black">
                {t('title', 'Respect de votre vie privée')}
              </h2>
            </div>
            <button
              onClick={handleRejectAll}
              className="text-aspexit-gray hover:text-aspexit-black transition-colors"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Description */}
          <p className="text-xs text-aspexit-gray mb-3 leading-relaxed">
            {t(
              'description',
              'Nous utilisons des cookies pour améliorer votre expérience sur notre site. Les cookies essentiels sont nécessaires au fonctionnement du site. Les cookies analytiques nous aident à comprendre comment vous utilisez notre site.'
            )}
          </p>

          {/* Détails (optionnel) */}
          {showDetails && (
            <div className="mb-3 p-3 bg-gray-50 rounded-lg space-y-3">
              {/* Cookies essentiels */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-aspexit-black text-xs mb-0.5">
                    {t('necessary.title', 'Cookies essentiels')}
                  </h3>
                  <p className="text-xs text-aspexit-gray">
                    {t(
                      'necessary.description',
                      'Nécessaires au fonctionnement du site (authentification, panier, préférences).'
                    )}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="w-4 h-4 rounded border-gray-300 text-aspexit-green focus:ring-aspexit-green cursor-not-allowed opacity-50 mt-0.5"
                />
              </div>

              {/* Cookies analytics */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-aspexit-black text-xs mb-0.5">
                    {t('analytics.title', 'Cookies analytiques')}
                  </h3>
                  <p className="text-xs text-aspexit-gray">
                    {t(
                      'analytics.description',
                      'Google Analytics pour comprendre l\'utilisation du site (données anonymisées).'
                    )}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={analyticsEnabled}
                  onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-aspexit-green focus:ring-aspexit-green cursor-pointer mt-0.5"
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleAcceptAll}
              size="sm"
              className="bg-aspexit-green text-white hover:bg-aspexit-green-dark text-xs"
            >
              {t('acceptAll', 'Tout accepter')}
            </Button>

            <Button
              onClick={() => setShowDetails(!showDetails)}
              size="sm"
              variant="outline"
              className="border-aspexit-green text-aspexit-green hover:bg-aspexit-green/10 text-xs"
            >
              <Settings className="w-3 h-3 mr-1" />
              {showDetails
                ? t('hideDetails', 'Masquer')
                : t('customize', 'Personnaliser')}
            </Button>

            {showDetails && (
              <Button
                onClick={handleSavePreferences}
                size="sm"
                variant="outline"
                className="text-xs"
              >
                {t('savePreferences', 'Enregistrer')}
              </Button>
            )}

            <Button onClick={handleRejectAll} size="sm" variant="outline" className="text-xs">
              {t('rejectAll', 'Tout refuser')}
            </Button>

            <Link
              to={`/${currentLocale}/confidentialite`}
              className="text-xs text-aspexit-gray underline hover:text-aspexit-green self-center ml-auto"
            >
              {t('privacyPolicy', 'Confidentialité')}
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
